// URS-F401: pestaña "Costes" del estudio.
// Contiene PVP del estudio, Nº de voluntarios (readonly, viene de Protocolo)
// y los dos importes de pago por voluntario (Pago 1 default + Pago 2 opcional
// para excepciones asignadas voluntario a voluntario en la pestaña Voluntarios).
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FloppyDisk, ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { formatEUR } from '../../utils/formatCurrency';
import { isAlwaysExcluded, isIntermediateVisit } from '../../utils/visitClassification';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CostesPanel({ studyId, lang }) {
  const [protocolData, setProtocolData] = useState({});
  const [numVolunteers, setNumVolunteers] = useState('');
  const [visits, setVisits] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  // URS-F406: coste total de voluntarios incluidos por tier.
  const [costsSummary, setCostsSummary] = useState(null);
  const [refreshingCosts, setRefreshingCosts] = useState(false);

  const lb = lang === 'es' ? {
    title: 'Costes del estudio',
    hint: 'El Pago 1 se aplica por defecto a todos los voluntarios. El Pago 2 solo aplica cuando lo marques explicitamente en la pestaña Voluntarios (excepciones).',
    pvp: 'PVP Estudio',
    pvp_placeholder: 'Precio de venta',
    num_vols: 'Nº de voluntarios',
    num_vols_hint: 'Valor traido de Protocolo (readonly)',
    pay1: 'Pago 1 por Voluntario',
    pay2: 'Pago 2 por Voluntario',
    pay3: 'Pago 3 por Voluntario',
    pay2_hint: 'Opcional. Se aplica solo a voluntarios que marques con "Pago 2".',
    pay3_hint: 'Opcional. Se aplica solo a voluntarios que marques con "Pago 3".',
    total_visits: 'Total Visitas Estudio',
    total_visits_real: 'Total Visitas Ejecutadas',
    total_visits_hint: 'Visitas computables × Nº voluntarios de protocolo. EA (Eventos Adversos) nunca se computa. Con toggle OFF se excluyen también VAL, SEG/SCR y sub-24H (T30MIN, T4H, T7D30MIN).',
    total_visits_real_hint: 'Suma de citas con estado "terminada" del estudio (misma lógica que Estadísticas).',
    price_per_visit_real: 'Precio por Visita Ejecutada',
    price_per_visit_real_hint: 'PVP Estudio dividido entre Total Visitas Ejecutadas. Refleja el coste vivo por visita.',
    executed_to_date: 'Ejecutado a día de hoy',
    executed_to_date_hint: 'Total Visitas Ejecutadas × Precio por Visita ejecutada. Coste ya devengado a fecha actual.',
    include_intermediate: 'Incluir visitas intermedias',
    computable: 'Visitas computables',
    price_per_visit: 'Precio por Visita teórico',
    price_per_visit_hint: 'PVP Estudio dividido entre Total Visitas Estudio. Cambia si activas visitas intermedias.',
    total_volunteer_cost: 'Coste total de voluntarios',
    total_volunteer_cost_hint: 'Suma de voluntarios INCLUIDOS × su Pago (P1/P2/P3).',
    refresh: 'Actualizar',
    updated_ago: 'Actualizado',
    save: 'Guardar',
    saved: 'Costes guardados',
    guard_first: 'Guarda el estudio primero',
  } : {
    title: 'Study costs',
    hint: 'Payment 1 applies by default. Payment 2 only applies to volunteers you explicitly mark as "Payment 2" in the Volunteers tab (exceptions).',
    pvp: 'Study PVP',
    pvp_placeholder: 'Sale price',
    num_vols: 'Volunteers',
    num_vols_hint: 'Value read from Protocol (readonly)',
    pay1: 'Payment 1 per Volunteer',
    pay2: 'Payment 2 per Volunteer',
    pay3: 'Payment 3 per Volunteer',
    pay2_hint: 'Optional. Only applies to volunteers marked as "Payment 2".',
    pay3_hint: 'Optional. Only applies to volunteers marked as "Payment 3".',
    total_visits: 'Total Study Visits',
    total_visits_real: 'Total Executed Visits',
    total_visits_hint: 'Computable visits × protocol Nº volunteers. EA is never counted. Toggle OFF also excludes VAL, SEG/SCR and sub-24H (T30MIN, T4H, T7D30MIN).',
    total_visits_real_hint: 'Sum of appointments with status "terminada" for this study (same logic as Statistics).',
    price_per_visit_real: 'Executed Price per Visit',
    price_per_visit_real_hint: 'Study PVP divided by Total Executed Visits. Live cost per visit.',
    executed_to_date: 'Executed to date',
    executed_to_date_hint: 'Total Executed Visits × Executed Price per Visit. Cost accrued to date.',
    include_intermediate: 'Include intermediate visits',
    computable: 'Computable visits',
    price_per_visit: 'Theoretical Price per Visit',
    price_per_visit_hint: 'Study PVP divided by Total Study Visits. Changes if intermediate visits toggle is on.',
    total_volunteer_cost: 'Total Volunteer Cost',
    total_volunteer_cost_hint: 'Sum of INCLUDED volunteers × their Payment (P1/P2/P3).',
    refresh: 'Refresh',
    updated_ago: 'Updated',
    save: 'Save',
    saved: 'Costs saved',
    guard_first: 'Save the study first',
  };

  useEffect(() => {
    if (!studyId) { setLoaded(true); return; }
    axios.get(`${API_URL}/api/studies/${studyId}`, { withCredentials: true })
      .then(({ data }) => {
        const pd = data.protocol_data || {};
        setProtocolData(pd);
        // Nº de participantes: buscar la clave que corresponde al campo
        // "Nº de participantes" (viene del banco de protocol-fields como field_id).
        // Guardamos una copia local solo para mostrarla.
        axios.get(`${API_URL}/api/config/protocol-fields`, { withCredentials: true })
          .then(({ data: fields }) => {
            const pf = (fields || []).find(f => (f.label || '').toLowerCase().includes('participantes'));
            if (pf) setNumVolunteers(pd[pf.field_id] || '');
          }).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
    // URS-F403: visitas del protocolo para calcular "Total Visitas Estudio".
    axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true })
      .then(({ data }) => setVisits(Array.isArray(data) ? data : []))
      .catch(() => setVisits([]));
  }, [studyId]);

  // URS-F406: fetch del coste total de voluntarios. Se dispara al montar y
  // tambien cuando el usuario pulsa el boton Actualizar o guarda cambios.
  const refreshCostsSummary = useCallback(async () => {
    if (!studyId) return;
    setRefreshingCosts(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/studies/${studyId}/costs-summary`, { withCredentials: true });
      setCostsSummary(data);
    } catch (err) {
      // silenciar: el usuario ya vera el estado vacio
    }
    setRefreshingCosts(false);
  }, [studyId]);

  useEffect(() => { refreshCostsSummary(); }, [refreshCostsSummary]);

  const setField = (key, val) => setProtocolData(p => ({ ...p, [key]: val }));

  // URS-F402: formato es-ES para el PVP (miles con "." y decimales con ",").
  // - Almacenamos siempre el numero "raw" (ej: "15000" o "15000.5") en protocol_data
  //   para que el backend siga leyendolo como Number sin cambios.
  // - Al mostrarlo, formateamos con Intl.NumberFormat('es-ES').
  // - Al escribir, permitimos digitos, un unico separador decimal (","), y toleramos
  //   puntos que el usuario tipee como separador de miles (los eliminamos).
  const formatEs = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return '';
    const s = String(raw);
    // Preservar coma decimal "en curso" (usuario acaba de tipear "." sin decimales aun)
    const trailingDecimal = s.endsWith('.') ? ',' : '';
    const num = Number(s);
    if (Number.isNaN(num)) return s.replace('.', ',');
    const decPart = s.split('.')[1] ?? '';
    const decCount = Math.min(decPart.replace(/0+$/, '').length || decPart.length, 2);
    const formatted = new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: decCount,
      maximumFractionDigits: 2,
      useGrouping: 'always',
    }).format(num);
    return formatted + trailingDecimal;
  };
  const parseEs = (str) => {
    if (!str) return '';
    // Quita cualquier caracter que no sea digito, coma o signo negativo.
    // Los "." tipeados como miles se descartan.
    let cleaned = String(str).replace(/[^\d,-]/g, '');
    // Si hay varias comas, dejamos solo la primera.
    const firstComma = cleaned.indexOf(',');
    if (firstComma !== -1) {
      cleaned = cleaned.slice(0, firstComma + 1) + cleaned.slice(firstComma + 1).replace(/,/g, '');
    }
    // Convertimos coma decimal a punto para almacenaje interno.
    return cleaned.replace(',', '.');
  };

  const handleSave = async () => {
    if (!studyId) { toast.error(lb.guard_first); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/studies/${studyId}`, { protocol_data: protocolData }, { withCredentials: true });
      toast.success(lb.saved);
      // URS-F406: refrescar coste total tras guardar (por si cambiaron los pagos).
      refreshCostsSummary();
    } catch (err) {
      toast.error(errText(err));
    }
    setSaving(false);
  };

  if (!loaded) return <div className="p-12 text-center text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>;

  // URS-F412: reglas de clasificacion delegadas a `utils/visitClassification.js`
  //   isAlwaysExcluded → EA (nunca cuenta)
  //   isIntermediateVisit → VAL/SEG/SCR/EVAL/MIN/H<24 (solo con toggle ON)
  const includeIntermediate = !!protocolData._include_intermediate_visits;
  const computableVisits = visits.filter(v => {
    if (isAlwaysExcluded(v.code)) return false;
    return includeIntermediate || !isIntermediateVisit(v.code);
  });
  const totalVisitsInProtocol = visits.filter(v => !isAlwaysExcluded(v.code)).length;
  const numV = parseInt(numVolunteers, 10);
  const numVolsSafe = Number.isNaN(numV) ? 0 : numV;
  const totalVisits = computableVisits.length * numVolsSafe;
  // URS-F411: Total Visitas Ejecutadas = SUM de citas TERMINADAS del estudio
  // cuyo visit_code sea computable (mismo criterio que Estadisticas).
  // Los datos vienen de /costs-summary.executed_visits_by_code.
  const executedByCode = costsSummary?.executed_visits_by_code || {};
  const totalVisitsReal = Object.entries(executedByCode).reduce((acc, [code, count]) => {
    if (isAlwaysExcluded(code)) return acc;
    if (!includeIntermediate && isIntermediateVisit(code)) return acc;
    return acc + Number(count || 0);
  }, 0);
  const includedCount = costsSummary?.included_count ?? 0;
  // URS-F404: Precio por Visita = PVP / Total Visitas Estudio.
  const pvpNum = Number(protocolData._pvp_estudio);
  const pricePerVisit = (
    !Number.isNaN(pvpNum) && pvpNum > 0 && totalVisits > 0
      ? pvpNum / totalVisits
      : null
  );
  // URS-F408: Precio por Visita real = PVP / Total Visitas Ejecutadas.
  const pricePerVisitReal = (
    !Number.isNaN(pvpNum) && pvpNum > 0 && totalVisitsReal > 0
      ? pvpNum / totalVisitsReal
      : null
  );
  // URS-F478: Ejecutado a dia de hoy = Total Visitas Ejecutadas x Precio
  // por Visita Ejecutada (antes multiplicaba por el teorico). Con este
  // cambio el valor coincide con el PVP total cuando hay al menos una
  // visita ejecutada, porque Precio por Visita Ejecutada = PVP / N ejec.
  const executedToDate = (
    pricePerVisitReal !== null && totalVisitsReal > 0
      ? pricePerVisitReal * totalVisitsReal
      : null
  );

  return (
    <div className="space-y-4" data-testid="costes-panel">
      <div className="bg-white border border-stone-200 rounded-sm p-5 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-heading font-semibold text-stone-800">{lb.title}</h3>
            <p className="text-xs text-stone-500 mt-0.5 max-w-2xl">{lb.hint}</p>
          </div>
          <Button
            data-testid="costes-save-btn"
            onClick={handleSave}
            disabled={saving || !studyId}
            className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2"
          >
            <FloppyDisk size={14} />{saving ? '...' : lb.save}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-stone-800">{lb.pvp}</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="costes-pvp"
                type="text"
                inputMode="decimal"
                value={formatEs(protocolData._pvp_estudio)}
                onChange={e => setField('_pvp_estudio', parseEs(e.target.value))}
                placeholder={lb.pvp_placeholder}
                className="h-9 bg-white border-stone-200 font-semibold text-right"
                disabled={!studyId}
              />
              <span className="text-sm text-stone-500 font-medium">€</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-stone-800">{lb.num_vols}</Label>
            <Input
              data-testid="costes-num-volunteers"
              type="number"
              value={numVolunteers || ''}
              readOnly
              disabled
              className="h-9 bg-stone-50 border-stone-200 font-semibold text-right cursor-not-allowed"
            />
            <p className="text-[11px] text-stone-400 italic">{lb.num_vols_hint}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-stone-800">{lb.pay1}</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="costes-pay1"
                type="text"
                inputMode="decimal"
                value={formatEs(protocolData._payment_per_volunteer)}
                onChange={e => setField('_payment_per_volunteer', parseEs(e.target.value))}
                placeholder="0,00"
                className="h-9 bg-white border-stone-200 font-semibold text-right"
                disabled={!studyId}
              />
              <span className="text-sm text-stone-500 font-medium">€</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-stone-800">{lb.pay2}</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="costes-pay2"
                type="text"
                inputMode="decimal"
                value={formatEs(protocolData._payment_per_volunteer_2)}
                onChange={e => setField('_payment_per_volunteer_2', parseEs(e.target.value))}
                placeholder="0,00"
                className="h-9 bg-white border-stone-200 font-semibold text-right"
                disabled={!studyId}
              />
              <span className="text-sm text-stone-500 font-medium">€</span>
            </div>
            <p className="text-[11px] text-stone-400 italic">{lb.pay2_hint}</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-stone-800">{lb.pay3}</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="costes-pay3"
                type="text"
                inputMode="decimal"
                value={formatEs(protocolData._payment_per_volunteer_3)}
                onChange={e => setField('_payment_per_volunteer_3', parseEs(e.target.value))}
                placeholder="0,00"
                className="h-9 bg-white border-stone-200 font-semibold text-right"
                disabled={!studyId}
              />
              <span className="text-sm text-stone-500 font-medium">€</span>
            </div>
            <p className="text-[11px] text-stone-400 italic">{lb.pay3_hint}</p>
          </div>
        </div>

        {/* URS-F406: Coste total de voluntarios (INCLUIDOS por tier). */}
        <div className="pt-2">
          {(() => {
            const summary = costsSummary;
            const bt = summary?.by_tier || { '1': 0, '2': 0, '3': 0 };
            const total = summary?.total_cost ?? 0;
            const totalIncl = summary?.included_count ?? 0;
            const ts = summary?.computed_at ? new Date(summary.computed_at) : null;
            const agoMin = ts ? Math.max(0, Math.round((Date.now() - ts.getTime()) / 60000)) : null;
            const breakdown = [];
            if (bt['1']) breakdown.push(`${bt['1']}×P1`);
            if (bt['2']) breakdown.push(`${bt['2']}×P2`);
            if (bt['3']) breakdown.push(`${bt['3']}×P3`);
            const agoLabel = agoMin === null
              ? '—'
              : agoMin === 0
                ? (lang === 'es' ? 'ahora mismo' : 'just now')
                : agoMin === 1
                  ? (lang === 'es' ? 'hace 1 min' : '1 min ago')
                  : agoMin < 60
                    ? (lang === 'es' ? `hace ${agoMin} min` : `${agoMin} min ago`)
                    : (lang === 'es' ? `hace ${Math.round(agoMin/60)} h` : `${Math.round(agoMin/60)} h ago`);
            return (
              <div className="bg-amber-50/50 border border-amber-200 rounded-sm p-4 space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <Label className="text-sm font-semibold text-stone-800">{lb.total_volunteer_cost}</Label>
                    <p className="text-[11px] text-stone-500 italic mt-0.5">{lb.total_volunteer_cost_hint}</p>
                  </div>
                  <div
                    data-testid="costes-total-volunteer-cost"
                    className="h-10 flex items-center justify-end px-4 min-w-[160px] bg-white border border-amber-300 rounded-sm font-mono font-bold text-lg text-amber-900"
                  >
                    {formatEUR(total)}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="costes-refresh-btn"
                    onClick={refreshCostsSummary}
                    disabled={refreshingCosts || !studyId}
                    className="h-9 gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-100 rounded-sm"
                    title={lb.refresh}
                  >
                    <ArrowsClockwise size={14} className={refreshingCosts ? 'animate-spin' : ''} />
                    {lb.refresh}
                  </Button>
                </div>
                <div className="flex items-center justify-between text-[11px] text-stone-500">
                  <span data-testid="costes-total-breakdown">
                    {totalIncl > 0
                      ? `${totalIncl} ${lang === 'es' ? 'incluidos' : 'included'} · ${breakdown.join(' + ') || '—'}`
                      : (lang === 'es' ? 'Sin voluntarios incluidos aun' : 'No included volunteers yet')}
                  </span>
                  <span className="italic">{lb.updated_ago}: {agoLabel}</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* URS-F403: Total Visitas Estudio con toggle de intermedias. */}
        <div className="pt-4 border-t border-stone-200 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-sm font-medium text-stone-800">{lb.total_visits}</Label>
              <p className="text-[11px] text-stone-400 italic max-w-2xl mt-0.5">{lb.total_visits_hint}</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="costes-toggle-intermediate-wrap">
              <span className="text-xs text-stone-600 font-medium">{lb.include_intermediate}</span>
              <Switch
                data-testid="costes-toggle-intermediate"
                checked={includeIntermediate}
                onCheckedChange={(v) => setField('_include_intermediate_visits', !!v)}
                disabled={!studyId}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-stone-500">{lb.computable}</Label>
              <div
                data-testid="costes-computable-visits"
                className="h-9 flex items-center justify-end px-3 bg-stone-50 border border-stone-200 rounded-sm font-mono text-sm text-stone-700"
              >
                {computableVisits.length}
                <span className="ml-2 text-[10px] text-stone-400">/ {totalVisitsInProtocol}</span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-stone-500">{lb.total_visits}</Label>
              <div
                data-testid="costes-total-visits"
                className="h-9 flex items-center justify-end px-3 bg-[#1f3e80]/5 border border-[#1f3e80]/20 rounded-sm font-mono font-semibold text-sm text-[#1f3e80]"
              >
                {new Intl.NumberFormat('es-ES', { useGrouping: 'always' }).format(totalVisits)}
              </div>
              {numVolsSafe === 0 && (
                <p className="text-[11px] text-amber-600 italic">
                  {lang === 'es' ? 'Configura el Nº de participantes en la pestaña Protocolo.' : 'Set Nº of participants in the Protocol tab.'}
                </p>
              )}
            </div>

            {/* URS-F404: Precio por Visita = PVP / Total Visitas */}
            <div className="space-y-1.5">
              <Label className="text-xs text-stone-500">{lb.price_per_visit}</Label>
              <div
                data-testid="costes-price-per-visit"
                className="h-9 flex items-center justify-end px-3 bg-emerald-50 border border-emerald-200 rounded-sm font-mono font-semibold text-sm text-emerald-800"
              >
                {pricePerVisit === null ? '—' : formatEUR(pricePerVisit)}
              </div>
              <p className="text-[11px] text-stone-400 italic">{lb.price_per_visit_hint}</p>
            </div>

            {/* URS-F407: Total Visitas Estudio real (con voluntarios INCLUIDOS). */}
            <div className="hidden md:block" />
            <div className="space-y-1.5">
              <Label className="text-xs text-stone-500">{lb.total_visits_real}</Label>
              <div
                data-testid="costes-total-visits-real"
                className="h-9 flex items-center justify-end px-3 bg-[#1f3e80]/10 border border-[#1f3e80]/30 rounded-sm font-mono font-semibold text-sm text-[#1f3e80]"
              >
                {new Intl.NumberFormat('es-ES', { useGrouping: 'always' }).format(totalVisitsReal)}
                <span className="ml-2 text-[10px] text-[#1f3e80]/60">{lang === 'es' ? 'terminadas' : 'terminated'}</span>
              </div>
              <p className="text-[11px] text-stone-400 italic">{lb.total_visits_real_hint}</p>
            </div>
            {/* URS-F408: Precio por Visita real */}
            <div className="space-y-1.5">
              <Label className="text-xs text-stone-500">{lb.price_per_visit_real}</Label>
              <div
                data-testid="costes-price-per-visit-real"
                className="h-9 flex items-center justify-end px-3 bg-emerald-100 border border-emerald-300 rounded-sm font-mono font-semibold text-sm text-emerald-900"
              >
                {pricePerVisitReal === null ? '—' : formatEUR(pricePerVisitReal)}
              </div>
              <p className="text-[11px] text-stone-400 italic">{lb.price_per_visit_real_hint}</p>
            </div>
            {/* URS-F478: Ejecutado a dia de hoy = Total Visitas Ejecutadas x
                Precio por Visita Ejecutada (antes teorico). Ubicado en la
                columna 3 (debajo de "Precio por Visita Ejecutada"). */}
            <div className="space-y-1.5 md:col-start-3">
              <Label className="text-xs text-stone-500">{lb.executed_to_date}</Label>
              <div
                data-testid="costes-executed-to-date"
                className="h-9 flex items-center justify-end px-3 bg-amber-100 border border-amber-300 rounded-sm font-mono font-semibold text-sm text-amber-900"
              >
                {executedToDate === null ? '—' : formatEUR(executedToDate)}
              </div>
              <p className="text-[11px] text-stone-400 italic">{lb.executed_to_date_hint}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
